import React from 'react';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { clone, get } from 'lodash';
import { connect } from 'react-redux';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import { setThematicSelectionList } from '_Resources/Cards/Actions/setThematicSelectionList';
import { useMutation } from 'react-apollo';
import SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/saveThematicsOverviewSettings';
import GET_THEMATICS_OVERVIEW_DATA_QUERY from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicsOverviewData';
import CategoryBreakdownDropdown from './_Components/CategoryBreakdownDropdown';
import ThematicSelectionDropdown from './_Components/ThematicSelectionDropdown';
import { getSelectedThematicsForSettings } from '../../utils';

const SelectsContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 3.5em;
  margin-right: 1em;
  align-items: flex-end;
  flex-wrap: wrap;
  & > div {
    margin-right: 0.5em;
  }

  & #test {
    margin-top: 0.5em;
  }
`;

const GroupedDropdownsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: 2em;
  margin-left: 3em;
  & div {
    margin-right: 0.5em;
  }
`;

const graphs = [
  {
    id: 'pie',
    label: 'Pie',
  },
  {
    id: 'donut',
    label: 'Donut',
  },
  {
    id: 'treemap',
    label: 'Treemap',
  },
  {
    id: 'sunburst',
    label: 'Sunburst',
  },
  {
    id: 'river',
    label: 'River',
  },
];

const thematicsToDisplay = [
  {
    id: 'thematics',
    label: 'Categories',
  },
  {
    id: 'subthematics',
    label: 'Subcategories',
  },
  {
    id: 'subsubthematics',
    label: 'Sub sub categories',
  },
];

const filtersToDisplay = [
  {
    id: 'mentionCount',
    label: 'Mentions',
  },
  {
    id: 'emotions',
    label: 'Emotions',
    children: [
      {
        id: 'happiness',
        label: 'Happiness',
      },
      {
        id: 'surprise',
        label: 'Surprise',
      },
      {
        id: 'calm',
        label: 'Calm',
      },
      {
        id: 'sadness',
        label: 'Sadness',
      },
      {
        id: 'fear',
        label: 'Fear',
      },
      {
        id: 'anger',
        label: 'Anger',
      },
      {
        id: 'disgust',
        label: 'Disgust',
      },
    ],
  },
];

type DropdownsContainerProps = {
  setGraphToDisplay: any;
  graphToDisplay: string;
  setLevel: any;
  setFilterToDisplay: any;
  setSubFilterToDisplay: any;
  setOnlySelectedCategories: any;
  t: any;
  settings: any;
  setSettings: any;
  thematicSelection: any;
  selectionList: Array<any>;
  dispatchSelectThematicSelection: any;
  projectId: number;
  filterKeys: any;
};

const DropdownsContainer = ({
  setGraphToDisplay,
  graphToDisplay,
  setLevel,
  setFilterToDisplay,
  setSubFilterToDisplay,
  setOnlySelectedCategories,
  t,
  settings,
  setSettings,
  thematicSelection,
  selectionList,
  dispatchSelectThematicSelection,
  projectId,
  filterKeys,
}: DropdownsContainerProps) => {
  const allThematics = {
    createdBy: {
      firstName: 'system',
      lastName: 'system',
      email: 'support@qemotion.com',
    },
    id: null,
    label: t('All thematics'),
    name: t('All thematics'),
    projectId,
    thematics: [],
  };

  const [saveThematicsOverviewSettings] = useMutation(SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION);

  const onThematicSelectionChange = selectThematicSelectionOption => {
    dispatchSelectThematicSelection(selectThematicSelectionOption);
    const cloneSettings = clone(settings);
    cloneSettings.selectedThematics = getSelectedThematicsForSettings(selectThematicSelectionOption.thematics);
    if (selectThematicSelectionOption.id !== null) {
      setOnlySelectedCategories(true);
    }
    if (selectThematicSelectionOption.id === null) {
      cloneSettings.selectedThematics = [];
      setOnlySelectedCategories(false);
    }
    saveThematicsOverviewSettings({
      variables: {
        projectId,
        settings: JSON.stringify(cloneSettings),
        thematicSelectionId: selectThematicSelectionOption?.id || null,
      },
      refetchQueries: [
        {
          query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
          variables: {
            ...filterKeys,
            thematicSelectionId: selectThematicSelectionOption?.id || null,
          },
        },
      ],
    });
  };

  return (
    <div>
      <SelectsContainer>
        <GroupedDropdownsContainer>
          <CategoryBreakdownDropdown
            id="graphToDisplayDropdown"
            label="Graph to display"
            setOptionToDisplay={setGraphToDisplay}
            options={graphs}
            setSubOptionToDisplay={null}
            t={t}
            setSettings={setSettings}
            settings={settings}
            settingsName="graphToDisplay"
            thematicSelection={thematicSelection}
          />
          <CategoryBreakdownDropdown
            id="filterToDisplayDropdown"
            label="Filter to display"
            setOptionToDisplay={setFilterToDisplay}
            setSubOptionToDisplay={setSubFilterToDisplay}
            options={filtersToDisplay}
            t={t}
            setSettings={setSettings}
            settings={settings}
            settingsName="filterToDisplay"
            thematicSelection={thematicSelection}
          />
        </GroupedDropdownsContainer>
        {graphToDisplay !== 'treemap' && graphToDisplay !== 'sunburst' && (
          <CategoryBreakdownDropdown
            id="orderByDropdownContainer"
            label="Level to display"
            setOptionToDisplay={setLevel}
            options={thematicsToDisplay}
            setSubOptionToDisplay={null}
            t={t}
            setSettings={setSettings}
            settingsName="levelToDisplay"
            settings={settings}
            thematicSelection={thematicSelection}
          />
        )}
        <div style={{ marginLeft: '0.5em' }}>
          <ThematicSelectionDropdown
            id="thematicSelectionDropdownContainer"
            label="Selected categories"
            t={t}
            options={[allThematics, ...selectionList]}
            thematicSelection={thematicSelection}
            setThematicSelection={onThematicSelectionChange}
            benchmark={false}
          />
        </div>
      </SelectsContainer>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchSelectThematicSelection: thematicSelection => {
    dispatch(selectThematicSelection(thematicSelection));
  },
  dispatchSetThematicSelectionList: thematicSelectionList => {
    dispatch(setThematicSelectionList(thematicSelectionList));
  },
});

const mapStateToProps = state => ({
  projectId: get(state, ['projects', 'currentProject', 'id']),
  filterKeys: get(state, ['filters', 'filterKeys']),
  selectionList: get(state, ['thematicSelections', 'list']),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('button')(DropdownsContainer));
